/**
 * BrokerApplicationKanban — Vertical pipeline with sub-stage columns
 * Epic 3B: Broker Application Workflow
 * Phases as collapsible rows. Within each phase, sub-stages are columns.
 * Drag-and-drop at the sub-stage level for precise transitions.
 * Drop zones: GREEN = valid, RED = invalid.
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { DragDropContext, Droppable, Draggable, type DropResult, type DragStart } from '@hello-pangea/dnd'
import { RefreshCw, Search, Plus, ChevronDown, ChevronRight, Archive } from 'lucide-react'
import {
  KANBAN_COLUMNS, STAGE_DISPLAY, PHASE_COLORS, getValidNextStages, TERMINAL_STAGES,
} from '../constants/applicationStages'
import {
  listApplications, updateApplicationStage,
  archiveApplication,
  type Application,
} from '../api/brokerApi'
import ApplicationCard from './ApplicationCard'
import ApplicationDetailDrawer from './ApplicationDetailDrawer'
import ArchivedApplicationsTable from './ArchivedApplicationsTable'

interface Props {
  token: string
}

export default function BrokerApplicationKanban({ token }: Props) {
  const navigate = useNavigate()
  const [applications, setApplications] = useState<Application[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedApp, setSelectedApp] = useState<Application | null>(null)
  const [collapsedPhases, setCollapsedPhases] = useState<Set<string>>(new Set())
  // Track valid drop target stages during drag
  const [validDropStages, setValidDropStages] = useState<Set<string>>(new Set())
  const [isDragging, setIsDragging] = useState(false)
  // Archive view
  const [showArchived, setShowArchived] = useState(false)
  const [archivedApps, setArchivedApps] = useState<Application[]>([])
  const [archivedLoading, setArchivedLoading] = useState(false)

  const loadApplications = useCallback(async () => {
    try {
      const res = await listApplications(token, { active: true })
      setApplications(res.applications || [])
    } catch {
      /* ignore */
    } finally {
      setLoading(false)
    }
  }, [token])

  const loadArchivedApplications = useCallback(async () => {
    setArchivedLoading(true)
    try {
      const res = await listApplications(token, { active: false })
      setArchivedApps(res.applications || [])
    } catch { /* ignore */ } finally {
      setArchivedLoading(false)
    }
  }, [token])

  useEffect(() => {
    loadApplications()
  }, [loadApplications])

  // Search filter
  const matchesSearch = (a: Application) => {
    if (!search) return true
    const s = search.toLowerCase()
    return (
      (a.customer_name || '').toLowerCase().includes(s) ||
      (a.lender_name || '').toLowerCase().includes(s) ||
      (a.property_address || '').toLowerCase().includes(s)
    )
  }

  const handleDragStart = (start: DragStart) => {
    const app = applications.find(a => a.id === start.draggableId)
    if (!app) return
    setIsDragging(true)

    // Calculate valid drop target stages (not just phases)
    const validNext = getValidNextStages(app.stage)
    setValidDropStages(new Set(validNext))

    // Auto-expand phases that contain valid targets
    setCollapsedPhases(prev => {
      const next = new Set(prev)
      for (const col of KANBAN_COLUMNS) {
        if (col.stages.some(s => validNext.includes(s))) {
          next.delete(col.id)
        }
      }
      return next
    })
  }

  const handleDragEnd = async (result: DropResult) => {
    setIsDragging(false)
    setValidDropStages(new Set())

    if (!result.destination) return
    const appId = result.draggableId
    const app = applications.find(a => a.id === appId)
    if (!app) return

    // droppableId is now the stage name (e.g. "awaiting_documents")
    const targetStage = result.destination.droppableId
    if (targetStage === app.stage) return

    // Validate transition
    const validNext = getValidNextStages(app.stage)
    if (!validNext.includes(targetStage)) {
      alert(`Cannot move "${STAGE_DISPLAY[app.stage]}" to "${STAGE_DISPLAY[targetStage] || targetStage}".`)
      return
    }

    // Optimistic update
    setApplications(prev => prev.map(a =>
      a.id === appId ? { ...a, stage: targetStage, stage_updated_at: new Date().toISOString() } : a
    ))

    try {
      await updateApplicationStage(token, appId, targetStage)
    } catch (err) {
      loadApplications()
      alert(err instanceof Error ? err.message : 'Failed to move stage')
    }
  }

  const handleArchive = async (app: Application) => {
    try {
      await archiveApplication(token, app.id)
      setApplications(prev => prev.filter(a => a.id !== app.id))
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to archive')
    }
  }

  const togglePhase = (phaseId: string) => {
    setCollapsedPhases(prev => {
      const next = new Set(prev)
      next.has(phaseId) ? next.delete(phaseId) : next.add(phaseId)
      return next
    })
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, lender, address..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button
          onClick={() => {
            if (showArchived) { setArchivedLoading(true); loadArchivedApplications() }
            else { setLoading(true); loadApplications() }
          }}
          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading || archivedLoading ? 'animate-spin' : ''}`} />
        </button>
        <button
          onClick={() => {
            const next = !showArchived
            setShowArchived(next)
            if (next) loadArchivedApplications()
          }}
          className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
            showArchived
              ? 'bg-gray-800 text-white border-gray-800'
              : 'text-gray-600 border-gray-300 hover:bg-gray-100'
          }`}
        >
          <Archive className="w-4 h-4" />
          {showArchived ? 'Show Active' : 'Archived'}
        </button>
        {!showArchived && (
          <button
            onClick={() => navigate('/broker/select-customer?from=applications')}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" /> New Application
          </button>
        )}
      </div>

      {/* Archived Table View */}
      {showArchived ? (
        <ArchivedApplicationsTable
          applications={archivedApps}
          loading={archivedLoading}
          token={token}
          onReactivated={() => { loadApplications(); loadArchivedApplications() }}
          onAppClick={setSelectedApp}
        />
      ) : (
      /* Vertical Pipeline with Sub-Stage Columns */
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="space-y-2 flex-1 overflow-y-auto">
          {KANBAN_COLUMNS.map((column) => {
            const color = PHASE_COLORS[column.id] || '#6B7280'
            const isCollapsed = collapsedPhases.has(column.id)
            const phaseApps = applications.filter(a => column.stages.includes(a.stage) && matchesSearch(a))
            const phaseHasValidDrop = isDragging && column.stages.some(s => validDropStages.has(s))
            const phaseHasNoValidDrop = isDragging && !phaseHasValidDrop

            let borderClass = 'border-gray-200 bg-white'
            if (phaseHasValidDrop) borderClass = 'border-green-400 bg-green-50/30 ring-1 ring-green-300'
            else if (phaseHasNoValidDrop) borderClass = 'border-red-200 bg-red-50/20'

            return (
              <div key={column.id} className={`rounded-lg border-2 transition-all ${borderClass}`}>
                {/* Phase Header */}
                <div
                  className="flex items-center gap-2 px-4 py-2.5 cursor-pointer select-none hover:bg-gray-50/50 rounded-t-lg"
                  onClick={() => !isDragging && togglePhase(column.id)}
                >
                  {isCollapsed
                    ? <ChevronRight className="w-4 h-4 text-gray-400" />
                    : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                  <span className="text-sm font-semibold text-gray-900">{column.label}</span>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {phaseApps.length}
                  </span>
                  {isDragging && (
                    <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${
                      phaseHasValidDrop ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'
                    }`}>
                      {phaseHasValidDrop ? 'Drop here' : 'Cannot drop'}
                    </span>
                  )}
                </div>

                {/* Sub-Stage Columns */}
                {!isCollapsed && (
                  <div className="flex overflow-x-auto px-2 pb-3 gap-0">
                    {column.stages.map((stage) => {
                      const stageApps = phaseApps.filter(a => a.stage === stage)
                      const isValidDrop = isDragging && validDropStages.has(stage)
                      const isInvalidDrop = isDragging && !validDropStages.has(stage)

                      return (
                        <Droppable key={stage} droppableId={stage} direction="vertical">
                          {(provided, snapshot) => {
                            let stageBg = 'bg-gray-50/50'
                            if (snapshot.isDraggingOver && isValidDrop) stageBg = 'bg-green-100 ring-2 ring-green-400'
                            else if (snapshot.isDraggingOver && isInvalidDrop) stageBg = 'bg-red-100 ring-2 ring-red-300'
                            else if (isValidDrop) stageBg = 'bg-green-50'
                            else if (isInvalidDrop) stageBg = 'bg-red-50/30'

                            return (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className={`flex-shrink-0 rounded-lg mx-1 p-2 transition-all min-h-[80px] ${stageBg}`}
                                style={{ minWidth: stageApps.length > 0 || isDragging ? '180px' : '120px' }}
                              >
                                {/* Sub-stage header */}
                                <div className="flex items-center gap-1.5 mb-2">
                                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color, opacity: 0.6 }} />
                                  <span className="text-[10px] font-medium text-gray-600 uppercase tracking-wide truncate">
                                    {STAGE_DISPLAY[stage] || stage}
                                  </span>
                                  {stageApps.length > 0 && (
                                    <span className="text-[10px] text-gray-400 ml-auto">{stageApps.length}</span>
                                  )}
                                </div>

                                {/* Cards */}
                                {stageApps.map((app, index) => (
                                  <Draggable key={app.id} draggableId={app.id} index={index}>
                                    {(dragProvided, dragSnapshot) => (
                                      <div
                                        ref={dragProvided.innerRef}
                                        {...dragProvided.draggableProps}
                                        {...dragProvided.dragHandleProps}
                                        className={`mb-2 ${dragSnapshot.isDragging ? 'opacity-90 rotate-1 scale-105' : ''}`}
                                      >
                                        <ApplicationCard
                                          application={app}
                                          onClick={setSelectedApp}
                                          compact
                                        />
                                        {TERMINAL_STAGES.includes(app.stage) && (
                                          <button
                                            onClick={(e) => { e.stopPropagation(); handleArchive(app) }}
                                            className="mt-1 w-full flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-gray-800 hover:bg-gray-200 rounded py-1 transition-colors"
                                            title="Archive this application"
                                          >
                                            <Archive className="w-3 h-3" /> Archive
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )
                          }}
                        </Droppable>
                      )
                    })}
                  </div>
                )}

                {/* Hidden droppable when collapsed — need one droppable per stage even when collapsed */}
                {isCollapsed && column.stages.map((stage) => (
                  <Droppable key={stage} droppableId={stage} direction="vertical">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} className="hidden">
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                ))}
              </div>
            )
          })}
        </div>
      </DragDropContext>
      )}

      {/* Detail Drawer */}
      {selectedApp && (
        <>
          <div
            className="fixed inset-0 bg-black/20 z-40"
            onClick={() => setSelectedApp(null)}
          />
          <ApplicationDetailDrawer
            application={selectedApp}
            token={token}
            onClose={() => setSelectedApp(null)}
            onStageChanged={() => {
              loadApplications()
              setSelectedApp(null)
            }}
            onApplicationUpdated={loadApplications}
          />
        </>
      )}
    </div>
  )
}
